import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'vb_chat_session';

const getSessionId = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const linkify = (text) =>
  text.split(/(https?:\/\/[^\s)]+)/g).map((part, i) =>
    /^https?:\/\//.test(part)
      ? <a key={i} href={part} className="underline text-[#d9fb06] break-all" target="_blank" rel="noopener noreferrer">{part.replace(/^https?:\/\//, '')}</a>
      : part
  );

const WELCOME = "Bonjour ! Je suis l'assistant ValidatedBoat. Une question sur nos services, nos tarifs ou l'analyse d'annonce ? Je vous réponds tout de suite.";

const ChatWidget = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!open || loaded) return;
    fetch(`${API_URL}/api/chat/${getSessionId()}`)
      .then((r) => r.json())
      .then((d) => setMessages(d.messages?.length ? d.messages : [{ role: 'assistant', content: WELCOME }]))
      .catch(() => setMessages([{ role: 'assistant', content: WELCOME }]))
      .finally(() => setLoaded(true));
  }, [open, loaded]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, open]);

  if (pathname.startsWith('/admin')) return null;

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || streaming || !loaded) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      if (!res.ok) throw new Error(res.status === 429 ? 'Trop de messages, réessayez dans un moment.' : 'Service indisponible.');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith('data: ') || part === 'data: [DONE]') continue;
          const evt = JSON.parse(part.slice(6));
          if (evt.error) throw new Error(evt.error);
          if (evt.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: 'assistant', content: copy[copy.length - 1].content + evt.delta };
              return copy;
            });
          }
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: 'assistant', content: err.message, error: true };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Ouvrir l'assistant"
          data-testid="chat-open-btn"
          className={`fixed ${pathname.startsWith('/bateaux-a-vendre/') ? 'bottom-24 lg:bottom-6' : 'bottom-6'} right-6 z-[60] flex items-center gap-2 bg-[#d9fb06] text-[#1a1c1b] rounded-full pl-4 pr-5 py-3 font-bold shadow-2xl hover:scale-105 transition-transform`}
        >
          <MessageCircle size={22} />
          <span className="hidden sm:inline">Une question ?</span>
        </button>
      )}

      {open && (
        <div
          className="fixed z-[60] bottom-0 right-0 sm:bottom-6 sm:right-6 w-full sm:w-[380px] h-[100dvh] sm:h-[560px] sm:max-h-[80vh] bg-[#1a1c1b] border border-[#3f4816] sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="chat-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-[#d9fb06] text-[#1a1c1b]">
            <div>
              <p className="font-black uppercase text-sm leading-tight">Assistant ValidatedBoat</p>
              <p className="text-[11px] opacity-80">Réponses instantanées · Nicolas reste joignable</p>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Fermer" data-testid="chat-close-btn" className="p-1 rounded-full hover:bg-black/10">
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] px-3 py-2 rounded-2xl whitespace-pre-wrap leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-[#d9fb06] text-[#1a1c1b] rounded-br-sm'
                      : m.error ? 'bg-red-500/20 text-red-200 border border-red-500/40' : 'bg-[#302f2c] text-[#dfddd6] rounded-bl-sm'
                  }`}
                >
                  {m.content ? linkify(m.content) : <Loader2 size={16} className="animate-spin text-[#d9fb06]" />}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={send} className="p-3 border-t border-[#3f4816] flex gap-2 bg-[#1a1c1b]">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Posez votre question…"
              maxLength={1500}
              disabled={!loaded}
              data-testid="chat-input"
              className="flex-1 bg-[#302f2c] border border-[#3f4816] rounded-full px-4 py-2 text-sm text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
            />
            <button
              type="submit"
              disabled={streaming || !loaded || !input.trim()}
              aria-label="Envoyer"
              data-testid="chat-send-btn"
              className="bg-[#d9fb06] text-[#1a1c1b] rounded-full w-10 h-10 flex items-center justify-center disabled:opacity-50 hover:bg-[#c4e205]"
            >
              {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </form>
          <p className="text-[10px] text-[#888680] text-center pb-2 px-3">Assistant automatisé : ne remplace pas l'avis de l'expert. Pas de données sensibles.</p>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
